<template>
  <el-container class="admin-shell">
    <div v-if="isMobile && mobileMenuOpen" class="admin-aside-mask" @click="mobileMenuOpen = false"></div>
    <el-aside width="232px" class="admin-aside" :class="{ 'mobile-layout': isMobile, 'mobile-open': mobileMenuOpen }">
      <div class="brand">
        <span class="brand-mark">旅</span>
        <div>
          <strong>惠享京程</strong>
          <small>业务管理后台</small>
        </div>
      </div>
      <el-menu
        ref="navigationMenu"
        class="side-menu"
        unique-opened
        :default-openeds="defaultOpenGroups"
        router
        :default-active="$route.meta.activeMenu || $route.path"
        background-color="#15171c"
        text-color="#c8ccd4"
        active-text-color="#ffffff"
        @select="closeMobileMenu"
      >
        <el-menu-item index="/dashboard"><i class="el-icon-data-analysis"></i><span slot="title">工作台</span></el-menu-item>
        <el-submenu v-for="group in menuGroups" :key="group.key" :index="group.key">
          <template slot="title"><i :class="group.icon"></i><span>{{ group.label }}</span><span v-if="group.key === 'business' && (unreadTotal || orderUnreadTotal)" class="group-unread-dot"></span></template>
          <el-menu-item v-for="item in group.items" :key="item.path" :index="item.path">
            <i :class="item.icon"></i>
            <span slot="title" class="menu-item-label"><span>{{ item.label }}</span><span v-if="item.path === '/business/consultations' && unreadTotal" class="menu-unread-badge">{{ unreadLabel }}</span><span v-if="item.path === '/business/records' && orderUnreadTotal" class="menu-unread-badge">{{ orderUnreadTotal > 99 ? '99+' : orderUnreadTotal }}</span></span>
          </el-menu-item>
        </el-submenu>
      </el-menu>
    </el-aside>

    <password-dialog :visible.sync="passwordVisible" @completed="passwordChanged" />
    <el-container>
      <el-header class="admin-header" height="64px">
        <div class="header-leading">
          <el-button v-if="isMobile" class="mobile-menu-button" icon="el-icon-menu" circle @click="mobileMenuOpen = true" />
          <el-breadcrumb separator="/">
            <el-breadcrumb-item>管理后台</el-breadcrumb-item>
            <el-breadcrumb-item v-if="currentNavigation">{{ currentNavigation.group.label }}</el-breadcrumb-item>
            <el-breadcrumb-item v-if="currentNavigation && $route.meta.activeMenu" :to="currentNavigation.item.path">{{ currentNavigation.item.label }}</el-breadcrumb-item>
            <el-breadcrumb-item>{{ pageTitle }}</el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="account">
          <el-button type="text" :icon="soundEnabled ? 'el-icon-bell' : 'el-icon-close-notification'" @click="toggleSound">
            {{ soundEnabled ? '提示音已开' : '提示音已关' }}
          </el-button>
          <span>{{ currentAdmin.name || "管理员" }}</span>
          <el-button type="text" @click="passwordVisible=true">修改密码</el-button>
          <el-button type="text" @click="logout">退出登录</el-button>
        </div>
      </el-header>
      <el-main class="admin-main">
        <router-view />
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import PasswordDialog from "../components/PasswordDialog.vue";
import { getCurrentUser, logout } from "../api/auth";
import http from "../api/http";
import { connect, disconnect, subscribe } from "../utils/realtime";
import adminStore from "../store/admin";
import { visibleNavigation, navigationForPath, hasPermission } from "../config/navigation";

export default {
  name: "AdminLayout",
  components: { PasswordDialog },
  data() {
    return {
      passwordVisible: false,
      unreadTotal: 0,
      orderUnreadTotal: 0,
      unreadRefreshTimer: null,
      isMobile: false,
      mobileMenuOpen: false,
      soundEnabled: window.localStorage.getItem("consultation_sound_enabled") !== "false",
      audioContext: null
    };
  },
  mounted() {
    this.updateViewport();
    window.addEventListener("resize", this.updateViewport);
    this.refreshCurrentAdmin();
    this.permissionRefreshTimer = window.setInterval(() => this.refreshCurrentAdmin(), 15000);
    this.$root.$on("order-payments-viewed", this.refreshOrderUnreadTotal);
    this.unsubscribeRealtime = subscribe(this.handleRealtimeEvent);
    connect();
    this.refreshUnreadTotal();
  },
  beforeDestroy() {
    this.$root.$off("order-payments-viewed", this.refreshOrderUnreadTotal);
    window.clearInterval(this.permissionRefreshTimer);
    window.removeEventListener("resize", this.updateViewport);
    if (this.unsubscribeRealtime) this.unsubscribeRealtime();
    if (this.unreadRefreshTimer) window.clearTimeout(this.unreadRefreshTimer);
    disconnect();
  },
  watch: {
    "$route.path"() {
      this.refreshCurrentAdmin();
      this.$nextTick(() => {
        if (this.currentNavigation && this.$refs.navigationMenu) this.$refs.navigationMenu.open(this.currentNavigation.group.key);
      });
    }
  },
  computed: {
    currentAdmin() { return adminStore.admin; },
    menuGroups() { return visibleNavigation(this.currentAdmin); },
    currentNavigation() { return navigationForPath(this.$route.meta.activeMenu || this.$route.path); },
    defaultOpenGroups() { return this.currentNavigation ? [this.currentNavigation.group.key] : this.menuGroups.slice(0, 1).map(group => group.key); },
    pageTitle() {
      return this.$route.meta.activeMenu ? this.$route.meta.title : (this.currentNavigation ? this.currentNavigation.item.label : this.$route.meta.title || "工作台");
    },
    unreadLabel() {
      return this.unreadTotal > 99 ? "99+" : String(this.unreadTotal);
    }
  },
  methods: {
    passwordChanged() { window.localStorage.removeItem("admin_token"); adminStore.clear(); this.$router.replace("/login"); },
    handleRealtimeEvent(payload) {
      if (!payload) return;
      if (payload.event === "order.created") {
        this.showBusinessNotice("新订单提醒", this.orderNoticeText(payload.data), "/business/records");
        return;
      }
      if (payload.event === "payment.paid") {
        this.refreshOrderUnreadTotal();
        const path = this.canAccess("payments") ? "/business/payments" : "/business/records";
        this.showBusinessNotice("新支付提醒", this.paymentNoticeText(payload.data), path, "success");
        return;
      }
      if (!["consultation.message", "consultation.read", "realtime.connected"].includes(payload.event)) return;
      if (payload.event === "consultation.message" && payload.data && payload.data.message && payload.data.message.sender_type === "user") {
        this.playMessageSound();
      }
      if (this.unreadRefreshTimer) window.clearTimeout(this.unreadRefreshTimer);
      this.unreadRefreshTimer = window.setTimeout(() => {
        this.unreadRefreshTimer = null;
        this.refreshUnreadTotal();
      }, 80);
    },
    orderNoticeText(data = {}) {
      const identity = data.order_no ? `订单 ${data.order_no}` : "有一笔新订单";
      return data.name ? `${identity}：${data.name}` : identity;
    },
    paymentNoticeText(data = {}) {
      const identity = data.payment_scene === "supplement"
        ? `额外费用${data.name ? `“${data.name}”` : ""}`
        : (data.payment_no ? `支付单 ${data.payment_no}` : "收到一笔新支付");
      const amount = Number(data.amount);
      return Number.isFinite(amount) && amount > 0 ? `${identity}，金额 ¥${amount.toFixed(2)}` : identity;
    },
    showBusinessNotice(title, message, path, type = "warning") {
      this.playMessageSound();
      const notice = this.$notify({
        title,
        message,
        type,
        duration: 10000,
        showClose: true,
        onClick: () => {
          if (this.$route.path !== path) this.$router.push(path);
          notice.close();
        }
      });
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
      if (!this.isMobile) this.mobileMenuOpen = false;
    },
    closeMobileMenu() {
      if (this.isMobile) this.mobileMenuOpen = false;
    },
    toggleSound() {
      this.soundEnabled = !this.soundEnabled;
      window.localStorage.setItem("consultation_sound_enabled", String(this.soundEnabled));
      if (this.soundEnabled) this.playMessageSound();
    },
    playMessageSound() {
      if (!this.soundEnabled) return;
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        if (!this.audioContext) this.audioContext = new AudioContext();
        if (this.audioContext.state === "suspended") this.audioContext.resume();
        const now = this.audioContext.currentTime;
        const oscillator = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(880, now);
        oscillator.frequency.setValueAtTime(1100, now + 0.1);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.18, now + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.24);
        oscillator.connect(gain);
        gain.connect(this.audioContext.destination);
        oscillator.start(now);
        oscillator.stop(now + 0.25);
      } catch (error) {
        // 浏览器未授权音频时保留角标提醒，不影响消息接收。
      }
    },
    async refreshOrderUnreadTotal() {
      if (!this.canAccess("service-orders") && !this.canAccess("consult-orders")) { this.orderUnreadTotal=0; return; }
      try {
        const res = await http.get("/business-records/payment-unread", { showErrorMessage: false });
        this.orderUnreadTotal = Number((res.data && res.data.unread_count) || 0);
      } catch (error) { /* Failed refresh preserves the last known unread state. */ }
    },
    async refreshUnreadTotal() {
      await this.refreshOrderUnreadTotal();
      if (!this.canAccess("consultations")) return;
      try {
        const res = await http.get("/consultations/unread-summary", { showErrorMessage: false });
        this.unreadTotal = Number((res.data && res.data.unread_count) || 0);
      } catch (error) {
        // 角标失败不阻断后台其他功能。
      }
    },
    canAccess(moduleName) {
      return hasPermission(this.currentAdmin, moduleName);
    },
    async refreshCurrentAdmin() {
      try {
        const res = await getCurrentUser();
        if (res.data && res.data.user) adminStore.setAdmin(res.data.user);
      } catch (error) {
        // 获取失败时保留本地缓存的账号信息，避免菜单被清空。
        return;
      }
      await this.refreshUnreadTotal();
    },
    async logout() {
      try {
        await logout();
      } catch (error) {
        // 本地退出仍需清理 token，避免后端异常时卡住用户。
      }
      disconnect();
      window.localStorage.removeItem("admin_token");
      adminStore.clear();
      this.$router.replace("/login");
    }
  }
};
</script>

<style scoped>
.menu-item-label {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  width: 146px;
}

.group-unread-dot { display: inline-block; width: 7px; height: 7px; margin-left: 10px; border-radius: 50%; background: #f56c6c; }
.side-menu ::v-deep .el-submenu .el-menu-item { padding-left: 40px !important; min-width: 0; }
.side-menu ::v-deep .el-submenu__title { font-weight: 600; }

.header-leading {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.mobile-menu-button {
  flex: 0 0 auto;
}

.admin-aside-mask {
  position: fixed;
  inset: 0;
  z-index: 1998;
  background: rgba(15, 18, 24, 0.46);
}

.admin-aside.mobile-layout {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 1999;
  transform: translateX(-100%);
  transition: transform 0.2s ease;
}

.admin-aside.mobile-layout.mobile-open {
  transform: translateX(0);
}

@media (max-width: 768px) {
  .account > span {
    display: none;
  }

  .account {
    gap: 8px;
  }

  .account .el-button {
    padding-left: 4px;
    padding-right: 4px;
  }
}

.menu-unread-badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  background: #f56c6c;
  border-radius: 9px;
  box-sizing: border-box;
}
</style>
